import { useMemo } from 'react';

import { i18n } from '~/utils';

import {
  createCsvParser,
  type DataImportDefinition,
  type DataImportFieldDefinition,
  type FormStateHandler,
  useTemplateFile,
} from '@vestigas/ui/organisms';

import {
  LedgerAccountImportForm,
  type LedgerAccountImportFormContext,
} from './LedgerAccountImportForm';
import {
  ledgerAccountImportDefaultColumnMapping,
  ledgerAccountImportHeaderFields,
  ledgerAccountImportMandatoryHeaderFields,
} from './ledgerAccountImportSchema';
import {
  zodLedgerAccountImportHeadersSchema,
  zodLedgerAccountImportRowsSchema,
} from './validations';

const TEMPLATE_FILE_PATH = '/ledger_account_import_template.csv';

/**
 * Accounting systems export a chart of accounts with either separator, and no
 * ledger account field holds a decimal number, so a comma cannot collide with
 * a German decimal mark inside a cell.
 */
const LEDGER_ACCOUNT_CSV_DELIMITERS = [';', ','] as const;

/**
 * Carries the concrete rows schema so `DataImportWizard` infers the parsed
 * row type; a bare `DataImportDefinition` would erase it to `unknown`.
 */
export type LedgerAccountImportConfig = DataImportDefinition<
  typeof zodLedgerAccountImportRowsSchema
>;

const mandatoryHeaderFieldSet = new Set<string>(
  ledgerAccountImportMandatoryHeaderFields,
);

const ledgerAccountImportFieldDefinitions: readonly DataImportFieldDefinition[] =
  ledgerAccountImportHeaderFields.map((field) => {
    const defaultSourceHeader = ledgerAccountImportDefaultColumnMapping[field];

    return {
      defaultSourceHeader,
      field,
      isDate: field === 'valid_from',
      isMandatory: mandatoryHeaderFieldSet.has(field),
      label: defaultSourceHeader,
    };
  });

const ledgerAccountImportConfig: LedgerAccountImportConfig = {
  content: {
    instructions: {
      text: [
        i18n.t(($) => $.import.instructions.uploadFile, {
          ns: 'ledgerAccounts',
        }),
      ],
    },
    renderAdditionalForm: (
      updateFormState: FormStateHandler,
      context: Record<string, unknown>,
    ) => (
      <LedgerAccountImportForm
        context={context as LedgerAccountImportFormContext}
        onFormChange={updateFormState}
      />
    ),
  },
  fields: { definitions: ledgerAccountImportFieldDefinitions },
  source: {
    acceptedFileTypes: ['.csv', '.xlsx'],
    // An account name legitimately contains " | ", e.g. a name copied from a
    // system that uses it as a separator; the default CSV parser would split
    // that into a list, which then fails `name: z.string()`.
    parsers: {
      csv: createCsvParser({
        delimitersToGuess: LEDGER_ACCOUNT_CSV_DELIMITERS,
        listSeparator: null,
      }),
    },
  },
  validation: {
    headerSchema: zodLedgerAccountImportHeadersSchema,
    rowSchema: zodLedgerAccountImportRowsSchema,
  },
};

/**
 * Import config with the CSV template fetched lazily via TanStack Query.
 * A module-level fetch would fail the whole route chunk when the template
 * is unreachable; here the download link simply appears once loaded.
 */
export function useLedgerAccountImportConfig(): LedgerAccountImportConfig {
  const { data: templateFile } = useTemplateFile(
    TEMPLATE_FILE_PATH,
    {},
    'text/csv',
  );

  return useMemo(
    () => ({
      ...ledgerAccountImportConfig,
      content: {
        ...ledgerAccountImportConfig.content,
        instructions: {
          ...ledgerAccountImportConfig.content.instructions,
          templateFile,
        },
      },
    }),
    [templateFile],
  );
}
